import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Search, Clock, ArrowRight, X } from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

// Filter Banner Component
const FilterBanner = ({ filterName, count, onClear, color = 'blue' }) => {
  const colorClasses = {
    purple: 'bg-purple-100 border-purple-300 text-purple-800',
  };
  return (
    <div className={`mb-4 p-3 rounded-lg border ${colorClasses[color] || colorClasses.purple} flex items-center justify-between`}>
      <span className="font-medium">
        Showing {count} {filterName} notice{count !== 1 ? 's' : ''}
      </span>
      <Button variant="ghost" size="sm" onClick={onClear} className="h-8">
        <X className="h-4 w-4 mr-1" /> Clear Filter
      </Button>
    </div>
  );
};

export default function NoticesTab({
  allNotices,
  filteredNotices,
  noticeSearch,
  setNoticeSearch,
  noticeStatusFilter,
  setNoticeStatusFilter,
  urlFilter,
  getFilterName,
  clearFilter,
  project
}) {
  const navigate = useNavigate();

  return (
    <>
      {/* Filter Banner */}
      {urlFilter && getFilterName && getFilterName(urlFilter) && (
        <FilterBanner 
          filterName={getFilterName(urlFilter)} 
          count={filteredNotices.length} 
          onClear={clearFilter}
          color="purple"
        />
      )}
      
      {/* Back Button */}
      <Button
        onClick={() => navigate('/')}
        variant="ghost"
        className="mb-4 text-slate-600 hover:text-slate-900"
        data-testid="notices-back-button"
      >
        ← Back to Dashboard
      </Button>
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-barlow font-semibold uppercase">Notices</h2>
        <PrintScheduleButton 
          moduleType="Notice"
          data={allNotices}
          project={project}
        />
      </div>

      {/* Filter Controls for Notices */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search notices..."
            value={noticeSearch}
            onChange={(e) => setNoticeSearch(e.target.value)}
            className="pl-9"
            data-testid="notice-search"
          />
        </div>
        <Select value={noticeStatusFilter} onValueChange={setNoticeStatusFilter}>
          <SelectTrigger className="w-40" data-testid="notice-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="determined">Determined</SelectItem>
          </SelectContent>
        </Select>
        {(noticeSearch || noticeStatusFilter !== 'all') && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => { setNoticeSearch(''); setNoticeStatusFilter('all'); }}
            className="text-slate-500"
          >
            <X className="h-4 w-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-slate-900">{allNotices.length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Total Notices</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-600">{allNotices.filter(e => e.notice_status === 'Draft').length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Draft</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-600">{allNotices.filter(e => e.notice_status === 'Submitted').length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Submitted</p>
          </CardContent>
        </Card>
        <Card className="rounded-sm">
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-600">{allNotices.filter(e => e.notice_status === 'Determined').length}</p>
            <p className="text-xs text-slate-500 uppercase font-medium">Determined</p>
          </CardContent>
        </Card>
      </div>

      {filteredNotices.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
          <Bell className="h-12 w-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-600 font-medium">
            {urlFilter || noticeSearch || noticeStatusFilter !== 'all' ? 'No Matching Notices' : 'No notices created yet'}
          </p>
          <p className="text-sm text-slate-500 mt-1">
            {urlFilter || noticeSearch || noticeStatusFilter !== 'all' ? 'No notices match the current filter' : 'Convert a delay event to a notice to see it here'}
          </p>
          {(urlFilter || noticeSearch || noticeStatusFilter !== 'all') && (
            <Button variant="outline" onClick={() => { clearFilter(); setNoticeSearch(''); setNoticeStatusFilter('all'); }} className="mt-4">
              Clear Filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredNotices.map((event) => (
            <div
              key={event.notice_id}
              onClick={() => navigate(`/notices/${event.notice_id}`)}
              className="bg-white border border-slate-200 rounded-sm p-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold uppercase">Notice for: {event.event_type}</h3>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      NOTICE
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-3">{event.narrative}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-mono">{(event.start_date || event.occurrence_date) 
                        ? new Date(event.start_date || event.occurrence_date).toLocaleDateString() 
                        : 'N/A'}</span>
                    </div>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5 text-secondary group-hover:translate-x-1 transition-transform" strokeWidth={2} />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
